import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadDispensedData, lockRXDetails, getLockedItems } from '../../Services/loadOrder'; // ⬅️ added getLockedItems
import MenuBar from './MenuBar';
import { DataGrid } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import "../../assets/css/home.css";
import toast ,{ Toaster} from 'react-hot-toast'; 
import { exportToExcel } from '../../utils/exportToExcel';
import ExcelImg from '../../assets/images/Excel_XLSX.png';
import Button from '@mui/material/Button';
import {FaFileExcel} from 'react-icons/fa';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const SubmittedClaims = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const navigate = useNavigate();
  const [names, setNames] = useState([]);
  const [selectedName, setSelectedName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  // Track currently locked TaskIds (optional but useful for quick checks / debugging)
  const [lockedTaskIds, setLockedTaskIds] = useState([]);

  const user = JSON.parse(localStorage.getItem('user'));

  // ⚙️ Columns (unchanged)
  const columns = [
    { field: 'authNo', headerName: 'Auth No', flex:1, headerClassName: 'pink-header',
      renderCell: (params) => (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: 1.3,
          textAlign: 'left',
          padding: '2px 4px',
          width: '100%',
          height: '100%',
        }}>
          {params.value}
        </div>
      ),
    },
    { field: 'from', headerName: 'From', flex:1, headerClassName: 'pink-header' },
    { field: 'submittedDate', headerName: 'Submitted Date', flex:1.4, headerClassName: 'pink-header' },
    { field: 'medStartDate', headerName: 'Med Start Date', flex:1, headerClassName: 'pink-header' },
    { field: 'memberId', headerName: 'Member ID', flex:1, headerClassName: 'pink-header' },
    { field: 'lastName', headerName: 'Last Name', flex:1, headerClassName: 'pink-header' },
    { field: 'firstName', headerName: 'First Name', flex:1, headerClassName: 'pink-header' },
    { field: 'priority', headerName: 'Priority', flex:1, headerClassName: 'pink-header' },
    { field: 'contract', headerName: 'Contract',flex:1.5, headerClassName: 'pink-header',
      renderCell: (params) => (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: 1.3,
          textAlign: 'left',
          padding: '2px 4px',
          width: '100%',
          height: '100%',
        }}>
          {params.value}
        </div>
      ),
    },
    { field: 'providerName', headerName: 'Provider Name', flex:1.5, headerClassName: 'pink-header',
      renderCell: (params) => (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: 1.3,
          textAlign: 'left',
          padding: '2px 4px',
          width: '100%',
          height: '100%',
        }}>
          {params.value}
        </div>
      ),
    },
    { field: 'estimatedcheckDate', headerName: 'Estimated Check Date', width: 142, headerClassName: 'pink-header' },
    { field: 'estimatedPaidAmount', headerName: 'Estimated Paid Amount', width: 142, headerClassName: 'pink-header' },
  ];

  // 🔎 Build search field list from columns (same pattern as PrescriptionGrid)
  useEffect(() => {
    const searchFields = columns.map(col => ({
      label: col.headerName,
      field: col.searchField || col.field
    }));
    setNames(searchFields);
  }, []);

  /**
   * 🔐 Bind & format rows with lock awareness (no column changes).
   * - Pulls lock list via getLockedItems(pharmacyId)
   * - Merges isLocked + lockedBy into each formatted row
   */
  const bindRows = async (data, pharmacyId, userId) => {
  try {
    if (!pharmacyId) {
      console.warn('[LOCK] Missing pharmacyId; skipping getLockedItems');
    }

    const lockedResponse = pharmacyId ? await getLockedItems(pharmacyId) : null;
    console.log('[LOCK] getLockedItems raw response:', lockedResponse);

    // Build a safe map: key is string TaskId
    const lockedTaskMap = new Map();

    if (lockedResponse?.success) {
      // Be flexible with where TaskDetail lives
      const taskList =
        lockedResponse?.data?.TaskDetail ??
        lockedResponse?.data?.TaskDetails ??
        lockedResponse?.data ??
        [];

      if (Array.isArray(taskList)) {
        taskList.forEach(item => {
          // Normalize both potential property names and coerce to string
          const tid = String(
            item.TaskId ?? item.TaskID ?? item.taskId ?? item.taskID ?? ''
          ).trim();
          const uid = item.UserID ?? item.UserId ?? item.userId ?? item.userID ?? '';
          if (tid) lockedTaskMap.set(tid, uid);
        });
      } else {
        console.warn('[LOCK] Unexpected TaskDetail shape:', taskList);
      }
    } else {
      console.warn('[LOCK] getLockedItems not successful or empty');
    }

    setLockedTaskIds(Array.from(lockedTaskMap.keys())); // optional

    // Map your grid rows and tag lock state. Normalize TaskID to string before Map.has
    const formattedRows = data.map((item, index) => {
      const taskIdStr = String(
        item.TaskID ?? item.TaskId ?? item.taskID ?? item.taskId ?? ''
      ).trim();

      return {
        id: index + 1,
        taskId: item.TaskID, // keep your original field as-is
        priority: item.Priority || '',
        authNo: item.AuthNo || '',
        from: item.From || '',
        submittedDate: item.SubmittedDate ? new Date(item.SubmittedDate).toLocaleDateString() : '',
        medStartDate: item.MedStartDate ? new Date(item.MedStartDate).toLocaleDateString() : '',
        memberId: item.MemberID,
        lastName: item.LastName,
        firstName: item.FirstName,
        contract: item.Contract,
        pharmacyId,
        userId,
        providerName: item.ProviderName,
        estimatedcheckDate: item.EstimatedCheckDate ? new Date(item.EstimatedCheckDate).toLocaleDateString() : '',
        estimatedPaidAmount: item.EstimatedPaidAmount,
        // 🔒 lock flags
        isLocked: lockedTaskMap.has(taskIdStr),
        lockedBy: lockedTaskMap.get(taskIdStr) || '',
      };
    });

    console.log('[LOCK] lockedTaskIds:', Array.from(lockedTaskMap.keys()));
    setRows(formattedRows);
  } catch (err) {
    console.error('Error binding rows with locks:', err);
    // Fallback: show data without lock flags
    const fallbackRows = data.map((item, index) => ({
      id: index + 1,
      taskId: item.TaskID,
      priority: item.Priority || '',
      authNo: item.AuthNo || '',
      from: item.From || '',
      submittedDate: item.SubmittedDate ? new Date(item.SubmittedDate).toLocaleDateString() : '',
      medStartDate: item.MedStartDate ? new Date(item.MedStartDate).toLocaleDateString() : '',
      memberId: item.MemberID,
      lastName: item.LastName,
      firstName: item.FirstName,
      contract: item.Contract,
      pharmacyId,
      userId,
      providerName: item.ProviderName,
      estimatedcheckDate: item.EstimatedCheckDate ? new Date(item.EstimatedCheckDate).toLocaleDateString() : '',
      estimatedPaidAmount: item.EstimatedPaidAmount,
      isLocked: false,
      lockedBy: '',
    }));
    setRows(fallbackRows);
  }
};


  // 🔎 Search
  const handleSearch = async () => {
    if (!selectedName || !searchText) {
      toast.error("Please select a value and enter text to search.");
      return;
    }

    const user = JSON.parse(localStorage.getItem('user'));
    const pharmacyId = user?.pharmacyId;
    const userId = user?.userId;

    if (!pharmacyId || !userId) {
      toast.error("Missing pharmacy or user info. Please login again.");
      navigate('/error-page');
      return;
    }

    setSearchLoading(true);
    try {
      const orderResponse = await loadDispensedData(1, pharmacyId, selectedName, searchText, "", "", userId);
      console.log("Calling loadDispensedData with params:", {
        Flag: 1,
        PharmID: pharmacyId,
        Column: selectedName,
        Value: searchText,
        UserID: userId,
      });

      if (orderResponse?.success && Array.isArray(orderResponse?.filterData)) {
        await bindRows(orderResponse.filterData, pharmacyId, userId);
      } else {
        setRows([]);
        toast.error("No data found for the search.");
      }
    } catch (error) {
      toast.error("Error searching cost share details.");
      navigate('/error-page');
    } finally {
      setSearchLoading(false);
    }
  };

  // 📥 Initial load (same API; just binding via lock-aware binder)
  useEffect(() => {
    const fetchData = async () => {
      const user = JSON.parse(localStorage.getItem('user'));
      const pharmacyId = user?.pharmacyId;
      const userId = user?.userId;
      if (!pharmacyId) {
        setLoading(false);
        return;
      }

      try {
        const orderResponse = await loadDispensedData(1, pharmacyId, "", "", "", "", userId);
        if (orderResponse?.success && Array.isArray(orderResponse?.data)) {
          await bindRows(orderResponse.data, pharmacyId, userId);
        } else {
          setRows([]);
        }
      } catch (e) {
        console.error(e);
        navigate('/error-page');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  // 🖱️ Row click: block if locked, otherwise proceed and attempt LOCK
  const handleRowClick = async (params) => {
    const row = params.row;

    const contract = row.contract;
    const taskId = row.taskId;
    const pharmacyId = row.pharmacyId;
    const userId = row.userId;

    if (!contract || !taskId || !pharmacyId || !userId) {
      toast.error("Missing required task or user data.");
      navigate('/error-page');
      return;
    }

    // 🔒 Respect lock state (matches PrescriptionGrid behavior)
    if (row.isLocked) {
      const by = row.lockedBy ? ` by ${row.lockedBy}` : '';
      toast.error(`This task is currently locked${by}.`);
      return;
    }

    try {
      const rxResponse = await lockRXDetails(contract, userId, taskId, pharmacyId, "LOCK");
      console.log("Prescription details response:", rxResponse?.data);

      if (rxResponse?.success) {
        navigate('/submitted-open-orders', {
          state: {
            taskId,
            contract,
            pharmacyId,
            userId,
          }
        });
      } else {
        toast.error(rxResponse?.message || "This task is currently being used by another user.");
      }
    } catch (err) {
      console.error("Failed to load prescription details:", err);
      toast.error("Error loading prescription details.");
      navigate('/error-page');
    }
  };

  return (
    <> <Toaster position="top-right"  />
      <div>
        <MenuBar />
        <div className='top-page-option'>
          <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center' }}>
            <a href="#/home" style={{ textDecoration: 'none', color: '#ec088c', display: 'inline-flex', alignItems: 'center' }}>
              <ChevronLeft color='#ec088c' size={18} />
              <span style={{ marginLeft: '5px' }}>Back</span>
            </a>
          </div>

          <div className='top-page-option-name' style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span> Dispensed Orders</span>
            <ChevronRight color='#ec088c' size={18} style={{ position: 'relative', top: '2px' }} />
            <span>Submitted Claims</span>

            <div style={{ display: 'flex', justifyContent:'right', 
              alignItems: 'center', gap: '8px',  marginLeft:'auto', marginRight:'6.2%', }}>
              {/* Dropdown, Textbox, and Search Button */}
              <FormControl size="small" sx={{ minWidth: 100, height: '30px', background: '#fff', borderRadius: '4px', marginRight: '10px' }}>
                <InputLabel sx={{ fontFamily: 'Calibri', marginBottom: '10px', background: '#fff', color: 'black', fontSize: '12px'}}>--Select--</InputLabel>
                <Select
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                  label="Select"
                  sx={{
                    fontFamily: 'Segoe ui',
                    fontSize: '12px',
                    border: '1px solid rgb(236, 151, 202)',
                    borderRadius: '15px',
                    height: '30px',
                    boxShadow: 'none',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ec008c',
                      boxShadow: '0 0 6px rgba(236, 0, 140, 0.4)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#ec008c',
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>--Select--</MenuItem>
                  <MenuItem value="Priority" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>Priority</MenuItem>
                  <MenuItem value="AuthNo" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>AuthNo</MenuItem>
                  <MenuItem value="SubmittedDate" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>SubmittedDate</MenuItem>
                  {/* <MenuItem value="TaskName" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>TaskName</MenuItem> */}
                  <MenuItem value="MemberID" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>MemberID</MenuItem>
                  <MenuItem value="LastName" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>LastName</MenuItem>
                  <MenuItem value="FirstName" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>FirstName</MenuItem>
                  <MenuItem value="Subject" sx={{fontSize: '12px',  '&:hover': { backgroundColor: '#f5f5f5' } }}>Subject</MenuItem>
                </Select>
              </FormControl>

              <input
                type="text"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                style={{
                  padding: '5px 5px',
                  borderRadius: '15px', 
                  border: '1px solid rgb(236, 151, 202)', outline: 'none',
                  boxShadow : '0 1px 2px rgb(236, 151, 202)',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.border = '1px solid #ec008c';
                  e.target.style.boxShadow = '0 0 6px rgba(236, 0, 140, 0.4)';
                }}
                onBlur={(e) => {
                  e.target.style.border = '1px solid rgb(236, 151, 202)';
                  e.target.style.boxShadow = '0 1px 2px rgb(236, 151, 202)';
                }}
              />
              <button
                onClick={handleSearch}
                disabled={searchLoading}
                style={{ padding: '6px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                Search
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{ padding: '5px 18px', borderRadius: '4px', background: '#ec008c', color: 'white', border: 'none', cursor: 'pointer' }}
              >
                View All
              </button>
            </div>
          </div>
        </div>

        <div style={{ height: 'auto', width: '85%', margin: 'auto' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            onRowClick={handleRowClick}
            localeText={{ noRowsLabel: 'No Records Found, Please search again.' }}
            getRowClassName={(params) => (params.row.isLocked ? 'locked-task-row' : '')} // 🔒 visual cue
            sx={{
              fontSize: '12px',
              fontFamily: 'Segoe UI',
              '& .pink-header': {
                color: '#fff',
                backgroundColor: '#ec008c',
                fontWeight: 'bold',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: 1.2,
                textAlign: 'center',
                padding: '8px 2px',
              },
              '& .locked-task-row': {
                backgroundColor: 'cyan !important',
                color: 'black'
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f5f5f5 !important',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none !important',
                boxShadow: 'none !important',
              },
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none !important',
                boxShadow: 'none !important',
              },
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '85%', margin: '10px auto 0 auto' }}>
          <Button
            variant="outlined"
            onClick={() => {
              const exportColumns = columns.map(({ field, headerName }) => ({ field, headerName }));
              exportToExcel(exportColumns, rows, 'SubmittedClaims');
            }}
            sx={{ mb: 1, minWidth: 0, p: 0.5 }}
          >
            <FaFileExcel style={{ width: 30, height: 35 , color: '#008000'}} title="Download XLSX" alt="Excel" />
          </Button>
        </div>

      </div>

      {showSpinner && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #EC088C',
            borderRadius: '50%',
            width: 48,
            height: 48,
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Your request is being processed</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
      {loading && !showSpinner && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #EC088C',
            borderRadius: '50%',
            width: 48,
            height: 48,
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Your request is being processed</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
      {searchLoading && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            border: '6px solid #f3f3f3',
            borderTop: '6px solid #EC088C',
            borderRadius: '50%',
            width: 48,
            height: 48,
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222'}}>Searching...</div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}
    </>
  );
};

export default SubmittedClaims;
